import { useEffect, useState } from "react";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import UnderlineButton from "@components/ui/buttons/UnderlineButton";
import { useAuthStore } from "@store/auth";
import { useDataStore } from "@store/user_org";
import { invitationsApi } from "@services/api/invitations";

/**
 * Shown when Firebase auth succeeded but the backend has no profile for this
 * account (the orphan case: invited under email A, signed in with Google/Apple
 * under email B). Self-service repair, no admin/dev needed:
 *   1. Try to auto-link a pending invitation matching the login email.
 *   2. Otherwise let the user paste their invitation link/code to link it.
 */
export default function RepairAccountScreen() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const fetchUserData = useDataStore((s) => s.fetchUserData);

  const [phase, setPhase] = useState<"trying" | "manual">("trying");
  const [invite, setInvite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract a web token from a pasted invitation link, or fall back to the raw
  // value (already a token/code).
  const parseInvite = (raw: string): { token?: string; code?: string } => {
    const v = raw.trim();
    if (!v) return {};
    try {
      const url = new URL(v);
      const token = url.searchParams.get("token");
      const code = url.searchParams.get("code");
      if (token) return { token };
      if (code) return { code };
    } catch {
      /* not a URL — treat as a raw token/code below */
    }
    return { token: v };
  };

  // Attempt the no-code automatic repair once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await invitationsApi.autoClaim();
        if (cancelled) return;
        if (user?.uid) await fetchUserData(user.uid);
      } catch {
        if (!cancelled) setPhase("manual");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, fetchUserData]);

  const handleManualSubmit = async () => {
    const parsed = parseInvite(invite);
    if (!parsed.token && !parsed.code) {
      setError("Collez le lien ou le code reçu dans votre e-mail d'invitation.");
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await invitationsApi.claim(parsed);
      if (user?.uid) await fetchUserData(user.uid);
    } catch (e: any) {
      const status = e?.response?.status;
      setError(
        status === 400
          ? "Invitation invalide ou expirée. Vérifiez le lien/code ou demandez une nouvelle invitation."
          : "La liaison a échoué. Réessayez ou contactez votre administrateur.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full h-screen flex flex-col items-center justify-center bg-neutral-50 px-6">
      <div className="max-w-400 w-full flex flex-col items-center gap-3">
        {phase === "trying" ? (
          <>
            <div className="text-hx3 font-bold">Connexion en cours…</div>
            <div className="text-hx1 text-neutral-400 text-center">
              Nous finalisons l'accès à votre compte.
            </div>
          </>
        ) : (
          <>
            <div className="text-hx3 font-bold text-center">Finaliser votre compte</div>
            <div className="text-hx1 text-neutral-400 text-center mb-2">
              Vous êtes connecté{user?.email ? ` (${user.email})` : ""}, mais ce compte n'est
              pas encore relié à votre invitation. Collez le lien ou le code reçu dans votre
              e-mail d'invitation pour terminer.
            </div>
            <TextField
              type="text"
              label="Lien ou code d'invitation"
              value={invite}
              placeholder="https://mbmo.hexaflow.fr/accept-invitation?token=…"
              onChange={(value) => setInvite(value)}
              className="mb-2"
              disabled={submitting}
            />
            {error && <div className="text-h2 text-red-500 w-full">{error}</div>}
            <Button
              variant="large"
              disabled={submitting}
              type="button"
              label={submitting ? "Liaison…" : "Relier mon compte"}
              onClick={handleManualSubmit}
            />
            <div className="text-h2 text-neutral-400 text-center mt-2">
              Vous n'avez pas reçu d'invitation ? Contactez votre administrateur ou
              contact@hexaflow.fr
            </div>
          </>
        )}
        <UnderlineButton label="Se déconnecter" className="mt-4" onClick={() => logout()} />
      </div>
    </div>
  );
}
